import { Comedian, PageType, getPageTypesForComedians } from "./comedian";
import { VenueEvents, trackEvent } from "@punchup/analytics";
import { logException, logValidationError } from "@punchup/sentry";

import { Database } from "../supabase.types";
import { SupabaseClient } from "@supabase/supabase-js";
import { VenuePagesRow } from "./venue_pages";
import dayjs from "@punchup/utils/dayjs";
import { getUser } from "../supabase/supabase";

export type UserFollowRow =
  Database["public"]["Tables"]["user_comedian_follows"]["Row"];

export async function getUserFollowsComedian(
  supabase: SupabaseClient<Database>,
  comedianId: string
): Promise<boolean> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) {
    return false;
  }
  const { status, data, error } = await supabase
    .from("user_comedian_follows")
    .select("user_id")
    .eq("user_id", user.id)
    .eq("comedian_id", comedianId)
    .maybeSingle();

  if (error) {
    logException(error, {
      extra: {
        data: "[getUserFollowsComedian] Error finding follow of user",
        comedian_id: comedianId,
        user: user.id,
      },
      tags: {
        supabase: true,
      },
    });
  } else if (status === 200) {
    if (data) {
      return true;
    } else {
      return false;
    }
  }

  logValidationError("[getUserFollowsComedian] Error finding follow of user", {
    extra: {
      status,
      comedian_id: comedianId,
      user: user.id,
    },
    tags: {
      supabase: true,
    },
  });
  throw new Error(`Error finding follow of ${comedianId} for user ${user.id}`);
}

export async function getFollowedComedians({
  supabase,
  callerIsServer = false,
}: {
  supabase: SupabaseClient<Database>;
  callerIsServer?: boolean;
}): Promise<{ comedian_id: string }[]> {
  let userId = "";
  if (callerIsServer) {
    const user = await getUser(supabase);
    if (!user) {
      throw new Error(
        "Supabase user must be logged in to get followed comedians."
      );
    }
    userId = user.id;
  } else {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.user) {
      throw new Error(
        "Supabase client must have an active session to get followed comedians."
      );
    }
    userId = session.user.id;
  }

  const { error, data } = await supabase
    .from("user_comedian_follows")
    .select("comedian_id")
    .eq("user_id", userId);

  if (error) {
    logException(error, {
      extra: {
        data: `[getFollowedComedians] Error getting followed comedians for user ${userId}`,
        userId,
      },
      tags: {
        supabase: true,
        userId,
      },
    });
    return [];
  }

  return data;
}

/**
 * A followed comedian enriched with its page type. The `comedians` table is
 * really a "pages" table — the `page_type` table overrides the default
 * "comedian" type for the minority of pages that are actually podcasts,
 * musicians, etc. `page_type` is always populated (defaulting to "comedian").
 */
export type ComedianWithPageType = Comedian & { page_type: PageType };

export async function getFollowedComediansAndTheirDetails(
  supabase: SupabaseClient<Database>
): Promise<ComedianWithPageType[]> {
  const filter: {
    user_id?: string;
  } = {};
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (user) {
    filter["user_id"] = user.id;
  }
  const { status, data, error } = await supabase
    .from("user_comedian_follows")
    .select(
      `
      comedian:comedians!inner (
        *
      )
    `
    )
    .match(filter)
    .lte("comedian.published_at", dayjs().toISOString());

  if (error) {
    console.error(error);
  } else if (status === 200) {
    if (data) {
      // Flatten the array and remove any null or undefined values
      const flattenedComedians = data
        .flatMap((item) => (item.comedian ? [item.comedian] : []))
        .filter(Boolean);

      // Attach each page's type so clients can label podcasts/musicians/etc.
      // differently from comedians. One batched query; absent rows default to
      // "comedian".
      const pageTypes = await getPageTypesForComedians(
        flattenedComedians.map((comedian) => comedian.id)
      );
      return flattenedComedians.map((comedian) => ({
        ...comedian,
        page_type: (pageTypes[comedian.id] ?? "comedian") as PageType,
      }));
    }
  }
  throw new Error(
    `Error getting user comedina follows and their comedians for a user`
  );
}

export enum FOLLOW_LOCATIONS {
  OTP_SIGNIN = "otp-signin",
  OAUTH_SIGNIN = "oauth-signin",
  FREE_CHECKOUT_ALREADY_LOGGED_IN = "free-checkout-already-logged-in",
  PAID_CHECKOUT_ALREADY_LOGGED_IN = "paid-checkout-already-logged-in",
  PAYMENT_FORM = "payment-form",
  SUGGESTED = "suggested",
  LIBRARY = "library",
  COMEDIAN_LANDING_PAGE = "comedian-landing-page",
  COMEDIAN_FOLLOW_PAGE = "comedian-follow-page",
  FEED_FEATURED_COMEDIAN_CARD = "feed-featured-comedian-card",
  FEED_SUGGESTED_COMEDIAN_CARD = "feed-suggested-comedian-card",
  FEED_VIDEO_CARD = "feed-video-card",
  SEARCH = "search",
  EVENT_LANDING = "event-landing",
  VENUE_PAGE = "venue-page",
  CLAP_REACTION_BUTTON = "clap-reaction-button",
  SHOWS_NEARBY = "shows-nearby",
  DIRECTORY = "directory",
  DISCOVER_PAGE = "discover-page",
}

// Follow a single venue page
export async function followVenue(
  supabase: SupabaseClient<Database>,
  venue: Pick<VenuePagesRow, "id" | "slug">,
  followLocation: FOLLOW_LOCATIONS
) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.user) {
    throw new Error(
      "Supabase client must have an active session to follow a venue."
    );
  }
  try {
    await supabase
      .from("user_venue_follows")
      .insert({ user_id: session.user.id, venue_page_id: venue.id })
      .throwOnError();
    trackEvent(VenueEvents.FOLLOWED_VENUE, {
      VenueId: venue.id,
      venueSlug: venue.slug,
      followLocation,
    });
  } catch (err) {
    logException(err, {
      data: `Error while attempting to follow venue page with id ${venue.id}, by user with id ${session.user.id}`,
    });
  }
}

// Unfollow a single venue page
export async function unfollowVenue(
  supabase: SupabaseClient<Database>,
  venue: Pick<VenuePagesRow, "id" | "slug">,
  unfollowLocation: FOLLOW_LOCATIONS
) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.user) {
    throw new Error(
      "Supabase client must have an active session to unfollow a venue."
    );
  }
  try {
    await supabase
      .from("user_venue_follows")
      .delete()
      .match({ user_id: session.user.id, venue_page_id: venue.id })
      .throwOnError();

    trackEvent(VenueEvents.UNFOLLOWED_VENUE, {
      VenueId: venue.id,
      venueSlug: venue.slug,
      unfollowLocation,
    });
  } catch (err) {
    logException(err, {
      data: `Error while attempting to unfollow venue page with id ${venue.id}, by user with id ${session.user.id}`,
    });
  }
}
